import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { getAnalysisDataNew } from "src/pages/Insights/serviceHelper";
import { prepareChartPayload } from "src/pages/Insights/transformer/dashboardUtils";
import { IChartResponse } from "src/pages/Insights/models/interfaces";
import "./style.css";
import { setDataUpdateModalMessage, setIsDataUpdateModalOpen } from "src/pages/Insights/states/sectionalSummarySlice";
import { setCurrentAnalysisResetState } from "src/pages/Insights/states/quantDashboardSlice";
import { handleInsightsErrorDetail } from "src/pages/Insights/transformer";
import { IResponse } from "src/pages/Studies/models/interfaces";

export interface IChartErrorProps {
	errorMessage: string;
	setChartData?: (value: IChartResponse | null) => void;
	setIsChartLoading?: (value: boolean) => void;
	errorType?: string;
	is_retry?: boolean;
}

const ChartError: React.FC<IChartErrorProps> = ({ errorMessage, setChartData, setIsChartLoading, is_retry }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { selectedStudy } = useSelector((state: RootState) => state.studiesSlice);
	const { globalFilters, localFilters, selectedAnalysisId, selectedSlideName } = useSelector((state: RootState) => state.quantDashboardSlice);

	/** Regenerate the chart after a failure. Also syncs reset-enabled flags
	 *  from the fresh response so the Reset button stays accurate. */
	const fetchChartsData = useCallback(async () => {
		if (!setChartData || !setIsChartLoading) return;

		const chartGfPayload = prepareChartPayload(globalFilters);
		const chartLfPayload = prepareChartPayload(localFilters);
		const chartPayload = [...chartGfPayload, ...chartLfPayload];

		const payload = {
			study_id: selectedStudy?.study_id,
			analysis_id: selectedAnalysisId,
			slide_name: selectedSlideName,
			split_by: chartPayload,
			active_config_id: selectedStudy?.insights_id,
		};
		try {
			setIsChartLoading(true);
			const response = (await getAnalysisDataNew(payload)) as IResponse;
			setChartData(response?.data);

			// Flags live on sections[0].charts[0] — keep Reset state in sync
			const firstChart = response?.data?.sections?.[0]?.charts?.[0];
			dispatch(
				setCurrentAnalysisResetState({
					analysisId: selectedAnalysisId ?? null,
					slideName: selectedSlideName ?? null,
					isResetEnabled: firstChart?.is_reset_enabled,
					isResetDisabled: firstChart?.is_reset_disabled,
				}),
			);

			setIsChartLoading(false);
		} catch (error) {
			console.error("Error fetching chart data:", error);
			handleInsightsErrorDetail(error, dispatch);
			setIsChartLoading(false);
		}
	}, [selectedStudy, selectedAnalysisId, setIsChartLoading, setChartData, globalFilters, localFilters, selectedSlideName]);

	return (
		<div className="h-full bg-white p-6 mx-4 relative">
			<div className="dashboard-chart-error-overlay flex flex-col items-center justify-center">
				<div className="dashboard-chart-error-message-content flex items-center gap-2 absolute text-base-1 font-normal">
					<div className="dashboard-chart-error flex items-start gap-1">
						<i className="material-symbols-outlined text-[1.5rem] leading-none flex-shrink-0 text-[--warning-red] mt-[2px]">info</i>
						<span className="font-semibold text-base-14 text-[--warning-red] tracking-normal text-left">
							{errorMessage ? errorMessage : t("Something went wrong. Please try again.")}
						</span>
					</div>
					{is_retry && (
						<button className="flex justify-center items-center gap-1 flex-shrink-0" aria-label={t("Regenerate chart")} onClick={fetchChartsData}>
							<i className="material-symbols-outlined text-[1.5rem] h-full flex items-center justify-center text-[--primary-color]">cycle</i>
							<span className="font-semibold text-base-14 text-[--primary-color] tracking-normal">{t("Regenerate")}</span>
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ChartError;